'use client'

import React, { useMemo, useState } from 'react'
import Image from 'next/image'
import { Box, Flex, SimpleGrid, Stack, Text, Title } from '@mantine/core'
import { motion } from 'framer-motion'
import {
    IconActivity,
    IconAlertTriangle,
    IconBed,
    IconBatteryCharging,
    IconBrain,
    IconCircleArrowRight,
    IconDroplet,
    IconFlame,
    IconFlask,
    IconHeart,
    IconPill,
    IconRefresh,
    IconShieldCog,
    IconStethoscope,
    IconUserHeart,
    IconWalk,
} from '@tabler/icons-react'
import type { Sku } from '@/types/interfaces/sku'
import type { SkuComponentConfigMap, SkuContentAccordionIconKey } from '@/types/enums/sku-component-types'
import { refreshTextVariantProps } from '@/components/Refresh/RefreshTypography'
import NumberedAccordionItem from '@/components/Refresh/NumberedAccordion/NumberedAccordionItem'
import { textFadeInVariantsNoTransition, staggerContainer } from '@/lib/animations'
import { MotionBox } from '@/components/shared/AnimatedComponents'
import { SkuGetStartedCta } from '@/components/Refresh/CategoryPages/SkuGetStartedCta'
import { PageLocation } from '@nile/mantine-theme/analytics'
import { appPath } from '@/lib/getPublicAppUrl'

type SkuContentAccordionConfig = SkuComponentConfigMap['skuContentAccordion']

const DEFAULT_ICON_SIZE = 32
const CUSTOM_ICON_SIZE = 45
const ICON_STROKE = 1.5

const ACCORDION_ICONS = {
    arrowRightCircle: IconCircleArrowRight,
    droplet: IconDroplet,
    flame: IconFlame,
    activity: IconActivity,
    heart: IconHeart,
    pill: IconPill,
    stethoscope: IconStethoscope,
    flask: IconFlask,
    bed: IconBed,
    refresh: IconRefresh,
    batteryCharging: IconBatteryCharging,
    brain: IconBrain,
    shieldCog: IconShieldCog,
    alertTriangle: IconAlertTriangle,
    userHeart: IconUserHeart,
    walk: IconWalk,
} as const satisfies Record<SkuContentAccordionIconKey, typeof IconCircleArrowRight>

function resolveAccordionIcon(key: SkuContentAccordionIconKey) {
    return ACCORDION_ICONS[key] ?? IconCircleArrowRight
}

function skuCategoryFallbackImage(category: string | undefined): string {
    const c = category?.toLowerCase()
    if (c === 'lab') return appPath('/self-serve/hero-image--labs.jpg')
    if (c === 'medication') return appPath('/self-serve/hero-image--medications.jpg')
    return appPath('/self-serve/hero-image.png')
}

/**
 * Resolves a product image URL for the left column; mirrors `SkuRefreshHeroImage` / `SkuHeroImageServer` fallbacks.
 */
function useLeftColumnImageSrc(
    leftImage: SkuContentAccordionConfig['leftImage'],
    sku: Sku,
): { src: string | import('next/image').StaticImageData; alt: string } | null {
    return useMemo(() => {
        if (!leftImage) return null

        if (leftImage.src) {
            return {
                src: leftImage.src,
                alt: leftImage.alt ?? sku.name ?? 'Product',
            }
        }

        if (leftImage.fromSku) {
            const alt = leftImage.alt ?? sku.name ?? 'Product'
            if (sku.s3_object_key && !sku.s3_object_key.startsWith('fallback-icons/')) {
                const url = `https://${process.env.NEXT_PUBLIC_ASSETS_DOMAIN}/${sku.s3_object_key}`
                return { src: url, alt }
            }
            return { src: skuCategoryFallbackImage(sku.category), alt }
        }

        return null
    }, [leftImage, sku])
}

function LeftColumnImage({ src, alt }: { src: string | import('next/image').StaticImageData; alt: string }) {
    const isStatic = typeof src === 'object' && src !== null && 'src' in src

    return (
        <Box
            pos="relative"
            w="100%"
            maw={132}
            style={{
                aspectRatio: '1 / 1',
                borderRadius: 16,
                overflow: 'hidden',
                background: '#ebdace',
                flexShrink: 0,
            }}
        >
            {isStatic ? (
                <Image src={src} alt={alt} fill sizes="132px" style={{ objectFit: 'contain' }} />
            ) : (
                <Image src={src} alt={alt} fill sizes="132px" style={{ objectFit: 'contain' }} unoptimized />
            )}
        </Box>
    )
}

export function ContentWAccordion({ sku, config }: { sku: Sku; config: SkuContentAccordionConfig }) {
    const {
        title,
        description,
        infoGrid,
        items,
        showItemIcons = true,
        defaultItemIcon = 'arrowRightCircle',
        leftImage,
        showGetStartedButton = false,
        getStartedButtonLabel,
        showAllContent: showAllContentConfig = false,
    } = config

    const showAllContent = showAllContentConfig || !items.some((item) => item.content?.trim())

    const [openIndex, setOpenIndex] = useState<number | null>(null)

    const gridCells = infoGrid?.filter((c) => c.label?.trim() || c.value?.trim()) ?? []
    const showInfoGrid = gridCells.length > 0
    const resolvedLeftImage = useLeftColumnImageSrc(showInfoGrid || showGetStartedButton ? undefined : leftImage, sku)

    return (
        <Box
            component={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px 0px' }}
            variants={staggerContainer}
            py={48}
            px={{ base: 16, sm: 'auto' }}
            maw={1400}
            mx="auto"
            w="100%"
        >
            <Flex
                direction={{ base: 'column', sm: 'row' }}
                gap={{ base: 32, sm: 60 }}
                align={{ base: 'stretch', sm: 'flex-start' }}
                wrap="nowrap"
            >
                <MotionBox variants={textFadeInVariantsNoTransition} style={{ flex: '1 1 42%', minWidth: 0 }}>
                    <Stack gap={24} align="flex-start">
                        {resolvedLeftImage ? (
                            <LeftColumnImage src={resolvedLeftImage.src} alt={resolvedLeftImage.alt} />
                        ) : null}

                        <Title
                            order={2}
                            c="gray.9"
                            lh="var(--gm-refresh-title-xl-lh)"
                            m={0}
                            fz={{ base: 'var(--gm-refresh-title-md)', sm: 'var(--gm-refresh-title-xl)' }}
                            lts={-1.6}
                            maw={620}
                        >
                            {title}
                        </Title>

                        {description ? (
                            <Text
                                {...refreshTextVariantProps.body}
                                c="gray.7"
                                fz={{ base: 'md', sm: 'lg' }}
                                lh="150%"
                                maw={620}
                            >
                                {description}
                            </Text>
                        ) : null}

                        {showInfoGrid ? (
                            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={24} verticalSpacing={24} maw={620} w="100%">
                                {gridCells.slice(0, 4).map((cell, index) => (
                                    <Stack key={`info-${index}`} gap={8}>
                                        <Text
                                            c="gray.9"
                                            fz={16}
                                            fw="var(--weight-display-small, 450)"
                                            lh="140%"
                                            lts={-0.16}
                                        >
                                            {cell.label}
                                        </Text>
                                        <Text
                                            c="gray.9"
                                            fz={24}
                                            fw="var(--weight-display-small, 450)"
                                            lh="140%"
                                            lts={-0.24}
                                        >
                                            {cell.value}
                                        </Text>
                                    </Stack>
                                ))}
                            </SimpleGrid>
                        ) : null}

                        {showGetStartedButton ? (
                            <SkuGetStartedCta
                                sku={sku}
                                label={getStartedButtonLabel}
                                pageLocation={PageLocation.DETAIL_PAGE}
                                theme="primary"
                                buttonSize="md"
                            />
                        ) : null}
                    </Stack>
                </MotionBox>

                <MotionBox
                    variants={textFadeInVariantsNoTransition}
                    style={{ flex: '1 1 58%', minWidth: 0, maxWidth: 600, margin: '0 auto' }}
                >
                    <Stack gap={0}>
                        {items.map((item, index) => {
                            const hasCustomIcon = !!item.icon
                            const iconSize = hasCustomIcon ? CUSTOM_ICON_SIZE : DEFAULT_ICON_SIZE
                            const IconComponent = resolveAccordionIcon(item.icon ?? defaultItemIcon)
                            return (
                                <NumberedAccordionItem
                                    key={`sku-acc-${index}`}
                                    number={index + 1}
                                    title={item.title}
                                    content={item.content}
                                    isOpen={openIndex === index}
                                    onToggle={() => setOpenIndex(openIndex === index ? null : index)}
                                    hideTopBorder={false}
                                    showNumber={false}
                                    alwaysOpen={showAllContent}
                                    titleFontWeight={showGetStartedButton || showAllContent ? 400 : undefined}
                                    leadingSlot={
                                        showItemIcons ? (
                                            <IconComponent
                                                color="var(--mantine-color-gray-9)"
                                                size={iconSize}
                                                stroke={ICON_STROKE}
                                                aria-hidden
                                            />
                                        ) : undefined
                                    }
                                    contentStyle={{
                                        fz: 'lg',
                                        fw: 500,
                                        lh: '140%',
                                        ml: showItemIcons ? iconSize + 24 : 0,
                                        pt: 12,
                                    }}
                                />
                            )
                        })}
                    </Stack>
                </MotionBox>
            </Flex>
        </Box>
    )
}
